import { type ReactNode, useMemo } from "react";
import { Outlet, useNavigation } from "react-router-dom";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";

import { useAuthContext } from "@covertree/auth";
import { Banner, BannerPortalName } from "@covertree/ui/Banner";

import SidebarView, { type SidebarProps } from "./SidebarView";
import { TopBar } from "./TopBar";
import { ContactSection } from "./SidebarView/ContactTooltip";

interface Props extends Omit<SidebarProps, "signOut"> {
  bannerPortalName?: BannerPortalName;
  enableBanner?: boolean;
  search?: ReactNode;
  logo: ReactNode;
  contacts?: ContactSection[];
  menu?: ReactNode;
}

export const PageLayout = ({
  bannerPortalName,
  enableBanner,
  navigationItems,
  bottomNavigationItems,
  onChangePage,
  search,
  logo,
  contacts,
  menu,
}: Props) => {
  const { state } = useNavigation();

  const { basicUserData, signOut } = useAuthContext();

  const sidebar = useMemo(
    () => (
      <SidebarView
        navigationItems={navigationItems}
        bottomNavigationItems={bottomNavigationItems}
        signOut={signOut}
        onChangePage={onChangePage}
        logo={logo}
        contacts={contacts}
      />
    ),
    [
      navigationItems,
      bottomNavigationItems,
      onChangePage,
      signOut,
      logo,
      contacts,
    ]
  );

  const banner = useMemo(
    () =>
      !!bannerPortalName && !!enableBanner ? (
        <Banner
          portalName={bannerPortalName!}
          userGroups={basicUserData?.groups}
        />
      ) : null,
    [bannerPortalName, enableBanner]
  );

  return (
    <>
      {state === "loading" && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: 8,
            zIndex: 10000,
          }}
        />
      )}
      <Container>
        <Grid container>
          <Grid
            item
            md={2}
            sx={{
              display: { xs: "none", md: "block" },
            }}>
            {sidebar}
          </Grid>
          <Grid
            item
            className="page-layout-content"
            xs={12}
            md={10}
            sx={{
              marginTop: 0,
              transition: "margin-top 250ms ease-out",
              "&:has(#banner)": {
                marginTop: 3,
              },
              "& #banner": {
                position: "relative",
              },
              ...(search || menu
                ? {
                    "&:not(:has(#banner))": {
                      "& .MuiTableContainer-root": {
                        maxHeight: { md: `calc(100vh - 340px)` },
                      },
                      "& #table-toolbar-container": {
                        "& + div .MuiTableContainer-root": {
                          maxHeight: { md: `calc(100vh - 380px)` },
                        },
                      },
                    },
                    "&:has(#banner)": {
                      marginTop: 3,
                      "& .MuiTableContainer-root": {
                        maxHeight: { md: `calc(100vh - 418px)` },
                      },
                    },
                  }
                : {}),
            }}>
            {banner}
            <TopBar search={search} menu={menu} sidebar={sidebar} logo={logo} />
            <Grid
              id="page-layout-table-container"
              item
              md={12}
              sx={{
                mt: 3,
                "& .MuiTableContainer-root": {
                  maxHeight: { md: `calc(100vh - 252px)` },
                },
                "& #table-toolbar-container + div .MuiTableContainer-root": {
                  maxHeight: { md: `calc(100vh - 292px)` },
                },
              }}>
              <Outlet />
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default PageLayout;
